<template>
  <MainLayout hide-route-header>
    <div class="max-w-3xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle v-if="isLoading">
            <div class="spinner"></div>
          </CardTitle>
          <CardTitle v-else> Upload Photos for Social Media Ads </CardTitle>
        </CardHeader>
        <CardContent>
          <p class="mb-4">
            Please upload at least 3-5 pictures of your team below. These photos do not have to be professional quality.
            The more candid the better.
          </p>
          <p class="text-gray-500 text-sm">Think team meetings, happy hours, holiday parties, etc.</p>
          <FileInput
            class="mb-12"
            name="files"
            limits="any file"
            :multiple="true"
            accept="image/*"
            :model-value="teamPhotos"
            @update:model-value="photosUploaded"
          />
          <div class="flex justify-center">
            <TButton color="indigo" :disabled="isUploadDisabled" @click="submitPhotos">Upload</TButton>
          </div>
        </CardContent>
      </Card>
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { onMounted, ref } from 'vue';
import MainLayout from '@/components/layouts/MainLayout/index.vue';
import TButton from '@/components/shared/TButton.vue';
import type { FacebookAdConfig } from '@/api/quickstart';
//@ts-ignore
import { Api } from '@/api';
import useMetaCoordinator from './useMetaCoordinator';
import { useRouter } from 'vue-router';
import { toast } from 'vue-sonner';

const { enforcePageAccess } = useMetaCoordinator();
const router = useRouter();

const facebookAdConfig = ref<FacebookAdConfig | null>(null);
const teamPhotos = ref<FileList | null>(null);
const isUploadDisabled = ref(true);
const isLoading = ref(true);

const photosUploaded = (photoList: FileList) => {
  teamPhotos.value = photoList;
  isUploadDisabled.value = false;
};

const submitPhotos = async () => {
  const errors = [];

  if (facebookAdConfig.value && teamPhotos.value) {
    const result = await Api.Quickstart.updateFacebookAdConfig({
      teamPhotos: teamPhotos.value,
    });
    if (result.status === 'error') errors.push(result.message || 'Unknown network error.');
  } else {
    if (!facebookAdConfig.value) errors.push("Team's facebook advertising configuration was not found.");
    if (!teamPhotos.value) errors.push('Submission attempted without any photos.');
  }

  if (errors.length > 0) {
    toast.error('Photo upload error', {
      action: { label: 'Dismiss' },
      description: errors.join(', '),
    });
  } else {
    router.push('/quickstart/meta/access');
    toast.success('Photos successfully uploaded', {
      action: { label: 'Dismiss' },
      description: 'Congrats, you have uploaded your social photos!',
    });
  }
};

onMounted(async () => {
  const facebookAdConfigResponse = await Api.Quickstart.getFacebookAdConfig();
  isLoading.value = false;
  await enforcePageAccess(facebookAdConfigResponse);
  if (facebookAdConfigResponse?.record) facebookAdConfig.value = facebookAdConfigResponse.record;
});
</script>
